'use client';
import { useGLTF } from '@react-three/drei';
import { MeshBasicMaterial, DoubleSide } from 'three';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTextureGPULoader } from '../../../_hooks/useTextureGPULoader';
import type * as THREE from 'three';
import { UseCanvas } from '../../../ThreeLayout/UseCanvas';
import { Suspense, useEffect } from 'react';
import dynamic from 'next/dynamic';

const glbPath = `/model/link_scene.glb`;
useGLTF.preload(glbPath);

export const linkModelAtom = atom<LinkModelNodes | undefined>(undefined);
export const linkModelLoadedAtom = atom<boolean>(false);

const LazyModelLoader = dynamic(async () => import('./ModelLoader'), {
	ssr: false,
});

export function LinkModelLoader() {
	return (
		<Suspense fallback={null}>
			<LazyModelLoader />
		</Suspense>
	);
}

export default function ModelLoader() {
	const loaded = useAtomValue(linkModelLoadedAtom);

	return (
		<>
			{!loaded && (
				<UseCanvas>
					<Loader />
				</UseCanvas>
			)}
		</>
	);
}

function Loader() {
	const setNodes = useSetAtom(linkModelAtom);
	const [modelLoaded, setModelLoaded] = useAtom(linkModelLoadedAtom);
	const { nodes } = useGLTF(glbPath) as unknown as { nodes: LinkModelNodes };

	const texturesLinks = {
		Dock: '/model/textures/dock_2048.jpg',
		Headband_Main: '/model/textures/headband_2048.jpg',
		Headset_on_Dock1: '/model/textures/headset_2048_1.jpg',
		Headset_on_Dock2: '/model/textures/headset_2048_2.jpg',
		Headset_Floor: '/model/textures/headset_2048_3.jpg',
		Hub: '/model/textures/hub_2048.jpg',
		Floor: '/model/textures/floor.png',
	};

	const textures: Record<keyof typeof texturesLinks, THREE.Texture> = useTextureGPULoader(texturesLinks, 0);
	useEffect(() => {
		if (modelLoaded) {
			return;
		}
		nodes.Dock.material = new MeshBasicMaterial({ map: textures.Dock, side: DoubleSide });
		nodes.Headband_Main.material = new MeshBasicMaterial({ map: textures.Headband_Main, side: DoubleSide });
		nodes.Headband_Headset.material = new MeshBasicMaterial({ map: textures.Headband_Main, side: DoubleSide });
		nodes.HeadsetDockMaterialFirst = new MeshBasicMaterial({ map: textures.Headset_on_Dock1, side: DoubleSide });
		nodes.HeadsetDockMaterialSecond = new MeshBasicMaterial({ map: textures.Headset_on_Dock2, side: DoubleSide });
		nodes.HeadsetFloorMaterial = new MeshBasicMaterial({ map: textures.Headset_Floor, side: DoubleSide });
		nodes.Hub.material = new MeshBasicMaterial({ map: textures.Hub, side: DoubleSide });
		nodes.Floor.material = new MeshBasicMaterial({ map: textures.Floor, transparent: true, opacity: 0.5, side: DoubleSide });

		if ('Scene' in nodes) delete nodes.Scene;

		setNodes(nodes);
		setModelLoaded(true);
	}, [nodes]);

	return <></>;
}

export interface BlenderMesh {
	geometry: THREE.BufferGeometry;
	material: THREE.MeshBasicMaterial;
}

export interface LinkModelNodes {
	Dock: BlenderMesh;
	Headband_Main: BlenderMesh;
	Headband_Headset: BlenderMesh;
	Headset: BlenderMesh;
	HeadsetDockMaterialFirst: THREE.MeshBasicMaterial;
	HeadsetDockMaterialSecond: THREE.MeshBasicMaterial;
	HeadsetFloorMaterial: THREE.MeshBasicMaterial;
	Hub: BlenderMesh;
	Floor: BlenderMesh;
}
